import request from 'superagent';
import { sessionStorageItemNameAppJWT } from './sessionStorageItemNames';
import { refreshJWT } from './notificationsPolling';
import { addCriticalBreadcrumbToSentry } from './sentryErrorLogging';
import DetectServerUpdateBoundary from '../components/DetectServerUpdateBoundary';
import {
  ErrorWithHTTPStatusCode,
  OutdatedServerVersionError,
  SessionExpiredError
} from './errors';


export default class RequestHandler {
  getQueryParams(queryParameters) {
    return {
      yesplanurl: this.yesplanurl,
      ...queryParameters
    };
  }

  static handleRequest(req, resultCallback, resolve, reject) {
    const jwt = sessionStorage.getItem(sessionStorageItemNameAppJWT);
    /* Wrapper function that ensures that the error is returned, such that ErrorBoundaries can act upon them. */
    const rejectWithError = (res, error) =>
      reject(
        (() => {
          resultCallback(res, error);
          return error;
        })()
      );
    if (jwt !== null) req.set('Authorization', `Bearer ${jwt}`);
    req.end((_error, res) => {
      const status = res ? res.status : 503;
      if (status >= 500 && status < 600) {
        addCriticalBreadcrumbToSentry(
          'routing',
          'Received response status between 500 and 600',
          { status, extra: res ? res.body : _error }
        );
        return rejectWithError(null, new ErrorWithHTTPStatusCode(status));
      }
      const refreshedJWT = res.header['x-refreshed-authorization-jwt'];
      let error = null;
      if (refreshedJWT) {
        sessionStorage.setItem(sessionStorageItemNameAppJWT, refreshedJWT);
        refreshJWT(refreshedJWT);
      }
      if (status === 440) return rejectWithError(res, new SessionExpiredError());
      if (status >= 400) error = { code: status };
      if (DetectServerUpdateBoundary.checkServerVersionNumber(res.get('Server')))
        resolve(resultCallback(res, error));
      else return rejectWithError(res, new OutdatedServerVersionError());
    });
  }

  get(path, headers, resultCallback) {
    return new Promise((resolve, reject) => {
      RequestHandler.handleRequest(
        request.get(path).set(this.getQueryParams(headers)),
        resultCallback,
        resolve,
        reject
      );
    });
  }

  getWithQueryParameters(path, headers, queryParameters, resultCallback) {
    return new Promise((resolve, reject) => {
      RequestHandler.handleRequest(
        request.get(path).set(this.getQueryParams(headers)).query(queryParameters),
        resultCallback,
        resolve,
        reject
      );
    });
  }

  put(path, queryParameters, body, resultCallback) {
    return new Promise((resolve, reject) => {
      RequestHandler.handleRequest(
        request
          .put(path)
          .type('json')
          .set(this.getQueryParams(queryParameters))
          .send(body),
        resultCallback,
        resolve,
        reject
      );
    });
  }

  putWithRetry(path, queryParameters, body, resultCallback, retries = 3) {
    return new Promise((resolve, reject) => {
      RequestHandler.handleRequest(
        request
          .put(path)
          .type('json')
          .set(this.getQueryParams(queryParameters))
          .send(body),
        (res, error) => {
          if (error && retries > 0) {
            return this.putWithRetry(
              path,
              queryParameters,
              body,
              resultCallback,
              retries - 1
            );
          }
          return resultCallback(res, error);
        },
        resolve,
        reject
      );
    });
  }

  post(path, queryParameters, body, resultCallback) {
    return new Promise((resolve, reject) => {
      RequestHandler.handleRequest(
        request
          .post(path)
          .type('json')
          .set(this.getQueryParams(queryParameters))
          .send(body),
        resultCallback,
        resolve,
        reject
      );
    });
  }

  static hasPagination(response) {
    return response.body.pagination && response.body.pagination.next !== undefined;
  }

  static getCurrentPageInPagination(urlString) {
    const pageString = new URL(urlString).searchParams.get('page');
    return Number.parseInt(pageString);
  }

  getFromPaginationURL(path, headers, queryParameters, nextPageURL, resultCallback) {
    const nextURL = new URL(nextPageURL);
    const paginationParams = new URLSearchParams(nextURL.search);
    if (paginationParams.has('page') && paginationParams.has('book')) {
      queryParameters.page = paginationParams.get('page');
      queryParameters.book = paginationParams.get('book');
    }
    return new Promise((resolve, reject) => {
      RequestHandler.handleRequest(
        request.get(path).set(this.getQueryParams(headers)).query(queryParameters),
        resultCallback,
        resolve,
        reject
      );
    });
  }
}
